import React, { Component } from 'react';
import styled from 'styled-components';
import MarketingVid from './MarketingVid';
import { Icon } from '@blueprintjs/core';
import LazyLoad from 'react-lazy-load';

const StyledHomepageFeaturesSecondary = styled.div`
  background: #fff;
  padding: 80px 15px;

  .carousel__wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .carousel__nav {
    ul {
      margin: 0;
      padding: 0;
      list-style: none;

      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }

    li {
      width: 300px;
      padding: 15px;
      cursor: pointer;
      user-select: none;
      opacity: 0.5;
      transition: all 120ms ease-in-out;
      position: relative;

      p {
        font-size: 14px;
        margin-bottom: 0;
        color: ${props => props.theme.colorGrayDarker};
      }

      h4 {
        margin-bottom: 8px;
        border-bottom: 1px solid ${props => props.theme.colorGrayLighter};
        padding-bottom: 10px;
      }

      @media (pointer: fine) and (hover: hover) {
        &:hover {
          opacity: 1;
        }
      }

      &.active {
        opacity: 1;

        h4 {
          font-weight: 600;
        }
      }

      .icon-bg {
        position: absolute;
        top: 17px;
        right: 20px;
        opacity: 0.3;
      }
    }
  }

  .carousel__content {
    margin-right: 30px;
    min-height: 400px;

    h4 {
      font-size: 1.2rem;
      font-weight: 600;
    }
  }

  .carousel__content-slide {
    display: none;
    padding-top: 30px;
    min-width: 600px;

    img {
      width: 100%;
      max-width: 700px;
    }

    p {
      display: none;
      max-width: 500px;
      font-size: 1.1rem;
      margin: 15px auto 0;
      width: 100%;
    }

    h4 {
      display: none;
      text-align: center;
    }

    .divider {
      display: none;
    }

    &.active {
      display: block;
    }
  }

  @media screen and (max-width: 1000px) {
    .carousel__wrapper {
      flex-direction: column;
      align-items: center;
      margin-top: -20px;
    }

    .carousel__nav {
      width: 100%;
      margin-bottom: 10px;
      order: 1;

      > ul {
        flex-direction: row;
        align-items: center;
        justify-content: center;
        overflow-x: auto;

        li {
          width: auto;
          padding: 10px;

          h4 {
            font-size: 14px;
            white-space: nowrap;
            border: none;
            padding: 0;
          }

          p {
            display: none;
          }

          &.active {

            h4 {
              color: ${props => props.theme.colorPrimary};
              position: relative;

              :after {
                content: '';
                width: 100%;
                position: absolute;
                height: 1px;
                left: 0;
                bottom: -8px;
                background-color: ${props => props.theme.colorTealDarker};
              }
            }
          }

          .icon-bg {
            display: none;
          }
        }
      }
    }

    .carousel__content {
      margin-right: 0;
      min-height: auto;
      order: 2;
    }

    .carousel__content-slide {
      min-width: 0;
      width: 100%;
      max-width: 600px;

      p {
        display: block;
      }
    }
  }

  @media screen and (max-width: 580px) {
    .carousel__nav {
      display: none;
    }

    .carousel__content-slide {
      display: block;
      margin-bottom: 30px;

      h4, .divider {
        display: block;
      }
    }
  }
`

class HomepageFeaturesSecondary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      current: 0,
      auto: true
    }
  }

  componentDidMount() {
    this.autoChange = setInterval(() => this.incrementSlides(), 6000);
  }

  componentWillUnmount() {
    clearInterval(this.autoChange);
  }

  incrementSlides = () => {
    // Only run if auto is still true
    if (this.state.auto) {
      if (this.state.current !== 2) {
        this.setState(state => ({
          current: state.current + 1
        }));
      } else {
        this.setState({
          current: 0
        });
      }
    }
  }

  changeSlide = index => {
    this.setState({
      current: index
    })
    // If user initiates slide change, then remove auto incrementing
    if (this.state.auto) {
      this.setState({
        auto: false
      })
    }
  }

  render() {
    return (
      <StyledHomepageFeaturesSecondary>
        <div className="marketing-section__header">
          <h2>From overwhelmed to ready</h2>
          <div className="divider divider--m"></div>
          <p>Thoughtful features make preparing for trips a breeze.</p>
        </div>
        <div className="carousel__wrapper">
          <div className="carousel__content">
            <div className={`carousel__content-slide ${this.state.current === 0 ? 'active' : ''}`}>
              <h4 className="with-shape"><div className="shape square bg-orange-dark"></div>Trip Monitoring</h4>
              <LazyLoad offsetVertical={100}>              
                <MarketingVid id="monitoring" src={process.env.PUBLIC_URL + '/feature-monitoring.mp4'} alt="Trip Alerts" />
              </LazyLoad>
              <p>We constantly monitor your destinations for you. Sudden severe weather? Visa requirements change? We&apos;ll let you know and also update your trip.</p>
              <div className="divider divider--m divider--thick"></div>
            </div>
            <div className={`carousel__content-slide ${this.state.current === 1 ? 'active' : ''}`}>
              <h4 className="with-shape"><div className="shape circle bg-pink-dark"></div>Recurring Items</h4>
              <LazyLoad offsetVertical={100}>              
                <MarketingVid id="recurring" src={process.env.PUBLIC_URL + '/feature-recurring.mp4'} alt="Recurring Items" />
              </LazyLoad>
              <p>Customize your trips with recurring to-dos and packing list items. Set conditions for them too: All trips? Int&apos;l only? Specific countries only?</p>
              <div className="divider divider--m divider--thick"></div>
            </div>
            <div className={`carousel__content-slide ${this.state.current === 2 ? 'active' : ''}`}>
              <h4 className="with-shape"><div className="shape triangle bc-yellow-dark"></div>Multi-Destination</h4>
              <LazyLoad offsetVertical={100}>              
                <MarketingVid id="multidest" src={process.env.PUBLIC_URL + '/feature-multidest.mp4'} alt="Multi-Destination" />
              </LazyLoad>
              <p>Create your perfect itinerary. Add up to 10 cities for a single trip and see them all in one constantly updated timeline with weather.</p>
            </div>
          </div>
          <div className="carousel__nav">
            <ul>
              <li className={`${this.state.current === 0 ? 'active' : ''}`} onClick={() => this.changeSlide(0)}>
                <h4 className="with-shape"><div className="shape triangle bc-orange-dark"></div>Trip Alerts</h4>
                <p>We constantly monitor your destinations for you. Sudden severe weather? Visa requirements change? We&apos;ll let you know and also update your trip.</p>
                <div className="icon-bg"><Icon icon="eye-open" iconSize={17} /></div>
              </li>
              <li className={`${this.state.current === 1 ? 'active' : ''}`} onClick={() => this.changeSlide(1)}>
                <h4 className="with-shape"><div className="shape parallelogram bg-yellow-dark"></div>Recurring Items</h4>
                <p>Customize your trips with recurring to-dos and packing list items. Set conditions for them too: All trips? Int&apos;l only? Specific countries only?</p>
                <div className="icon-bg"><Icon icon="repeat" iconSize={14} /></div>
              </li>
              <li className={`${this.state.current === 2 ? 'active' : ''}`} onClick={() => this.changeSlide(2)}>
                <h4 className="with-shape"><div className="shape circle bg-green-dark"></div>Multi-Destination</h4>
                <p>Create your perfect itinerary. Add up to 10 cities for a single trip and see them all in one constantly updated timeline with weather.</p>
                <div className="icon-bg"><Icon icon="layout" iconSize={14} /></div>
              </li>
            </ul>
          </div>
        </div>
      </StyledHomepageFeaturesSecondary>
    );
  }
}

export default HomepageFeaturesSecondary;